/**
 * Custom hook for managing the complete breeding workflow
 */

import { useState, useCallback, useEffect } from 'react';
import type {
  WorkflowState,
  WorkflowStep,
  Plant,
  BreedingNotes,
  BreedingWorkflowResult,
  SeedRoom,
  BreedingWorkflowInput,
  StrainProgression,
} from '../types/breeding';
import { generateF1HybridStrainName } from '../services/breedingService';
import * as api from '../services/breedingApiService';

type WorkflowView = 'form' | 'plantSelection' | 'hybridReview' | 'progress' | 'summary';
type PlantSelectionPurpose = 'self' | 'hybridSeedParent' | 'hybridPollenDonor';

interface UseBreedingWorkflowOptions {
  summaryDelay?: number; // Delay in ms before showing summary (default: 1000)
}

interface PlantSelectionContext {
  purpose: PlantSelectionPurpose;
  input: BreedingWorkflowInput;
  seedParent?: Plant;
}

interface HybridReviewDraft {
  input: BreedingWorkflowInput;
  seedParent: Plant;
  pollenDonor: Plant;
  generatedName: string;
  finalName: string;
}

function normalizeWorkflowInput(
  inputOrBarcode: BreedingWorkflowInput | string,
  seedCount?: number,
  roomId?: number,
  notes: BreedingNotes | null = null
): BreedingWorkflowInput {
  if (typeof inputOrBarcode !== 'string') {
    return inputOrBarcode;
  }

  return {
    mode: 'self',
    barcode: inputOrBarcode,
    seedCount: seedCount ?? 0,
    roomId: roomId ?? 0,
    notes,
  };
}

function emptyDestroyState() {
  return {
    status: 'idle' as const,
    decision: null,
    error: '',
    result: null,
  };
}

function buildInitialState(seedRooms: SeedRoom[] = []): WorkflowState {
  return {
    mode: 'self',
    selectedPlant: null,
    originalStrain: null,
    newStrain: null,
    pollenDonorPlant: null,
    pollenDonorStrain: null,
    generatedStrainName: '',
    strainNameOverride: '',
    seedCount: 0,
    roomId: seedRooms[0]?.id || null,
    seedRooms,
    notes: null,
    breedingRunId: null,
    breedingRunData: null,
    destroy: emptyDestroyState(),
  };
}

export function useBreedingWorkflow(options: UseBreedingWorkflowOptions = {}) {
  const { summaryDelay = 1000 } = options;

  // Core state
  const [state, setState] = useState<WorkflowState>(() => buildInitialState());

  // UI state
  const [currentView, setCurrentView] = useState<WorkflowView>('form');
  const [workflowSteps, setWorkflowSteps] = useState<WorkflowStep[]>([]);
  const [multiplePlants, setMultiplePlants] = useState<Plant[]>([]);
  const [selectionContext, setSelectionContext] = useState<PlantSelectionContext | null>(null);
  const [hybridReview, setHybridReview] = useState<HybridReviewDraft | null>(null);
  const [error, setError] = useState('');

  // Load seed rooms on mount
  useEffect(() => {
    loadSeedRooms();
  }, []);

  const loadSeedRooms = useCallback(async () => {
    try {
      const rooms = await api.loadSeedRooms();
      setState((prev) => ({ ...prev, seedRooms: rooms, roomId: rooms[0]?.id || null }));
    } catch (err) {
      console.error('Failed to load seed rooms:', err);
      // Set default room if API fails
      setState((prev) => ({
        ...prev,
        seedRooms: [{ id: 9, name: 'Room 9 (Default)' }],
        roomId: 9,
      }));
    }
  }, []);

  // Step management
  const addStep = useCallback((title: string, message: string, status: WorkflowStep['status'] = 'processing') => {
    const step: WorkflowStep = {
      id: `step-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      title,
      message,
      status,
    };
    setWorkflowSteps((prev) => [...prev, step]);
    return step.id;
  }, []);

  const updateStep = useCallback(
    (stepId: string, title: string, message: string, status: WorkflowStep['status']) => {
      setWorkflowSteps((prev) =>
        prev.map((step) =>
          step.id === stepId ? { ...step, title, message, status } : step
        )
      );
    },
    []
  );

  const getRoomName = useCallback(
    (roomId: number, seedRooms: SeedRoom[]) => {
      const selectedRoom = seedRooms.find((r) => r.id === roomId);
      return selectedRoom ? selectedRoom.name : `Room ${roomId}`;
    },
    []
  );

  const createOrGetStrain = useCallback(
    async (strainName: string, stepTitle = 'Creating strain') => {
      const step = addStep(stepTitle, `Checking if "${strainName}" exists...`);

      try {
        const existingStrain = await api.getStrain(strainName);
        updateStep(
          step,
          'Using existing strain',
          `Strain "${strainName}" already exists (ID: ${existingStrain.id})`,
          'completed'
        );
        return existingStrain.id;
      } catch {
        updateStep(step, 'Creating strain', `Creating new strain "${strainName}"...`, 'processing');
        const newStrain = await api.createStrain(strainName);
        updateStep(
          step,
          'Strain created',
          `Created new strain "${strainName}" (ID: ${newStrain.id})`,
          'completed'
        );
        return newStrain.id;
      }
    },
    [addStep, updateStep]
  );

  const executeSelfWorkflow = useCallback(
    async (plant: Plant, seedCount: number, roomId: number, notes: BreedingNotes | null, seedRooms: SeedRoom[]) => {
      try {
        // Step 2: Validate plant
        const step2 = addStep('Validating plant', 'Checking if plant can be used for breeding...');

        const validation = await api.validatePlant(plant.Id);

        if (!validation.valid) {
          updateStep(step2, 'Validation failed', validation.error || 'Plant is not valid', 'error');
          setError(validation.error || 'Plant is not valid');
          setTimeout(() => setCurrentView('form'), 3000);
          return;
        }

        updateStep(step2, 'Plant validated', 'Plant is eligible for breeding', 'completed');
        const originalStrain = { id: plant.StrainId, name: plant.StrainName };
        setState((prev) => ({
          ...prev,
          mode: 'self',
          selectedPlant: plant,
          originalStrain,
          pollenDonorPlant: null,
          pollenDonorStrain: null,
          generatedStrainName: '',
          strainNameOverride: '',
        }));

        // Step 3: Generate strain name
        const step3 = addStep('Generating strain name', 'Creating S-number progression...');

        const strainData = await api.generateStrainName(plant.StrainName);

        setState((prev) => ({ ...prev, newStrain: { ...strainData, id: 0 } })); // id will be set later
        updateStep(
          step3,
          'Strain name generated',
          `${strainData.originalName} -> ${strainData.newName} (Generation S${strainData.generation})`,
          'completed'
        );

        const strainId = await createOrGetStrain(strainData.newName);

        setState((prev) => ({
          ...prev,
          newStrain: prev.newStrain ? { ...prev.newStrain, id: strainId } : null,
        }));

        // Step 5: Toggle mother status (if needed)
        const step5 = addStep('Checking mother status', 'Verifying plant mother status...');

        if (plant.IsMother) {
          updateStep(step5, 'Already a mother', 'Plant is already a mother plant, skipping', 'completed');
        } else {
          updateStep(step5, 'Converting to mother', 'Toggling mother plant status...', 'processing');
          await api.toggleMotherStatus(plant.Id);
          updateStep(step5, 'Mother status updated', 'Plant converted to mother status', 'completed');
        }

        // Step 6: Reassign to new strain
        const step6 = addStep('Reassigning strain', `Temporarily assigning to "${strainData.newName}"...`);

        await api.reassignStrain(plant.Id, strainId);
        updateStep(step6, 'Strain reassigned', `Mother plant reassigned to "${strainData.newName}"`, 'completed');

        // Step 7: Create seeds
        const roomName = getRoomName(roomId, seedRooms);
        const step7 = addStep('Creating seeds', `Creating ${seedCount} seeds in ${roomName}...`);

        await api.createSeeds(plant.Id, seedCount, roomId);
        updateStep(step7, 'Seeds created', `Created ${seedCount} seeds in ${roomName}`, 'completed');

        // Step 8: Restore original strain
        const step8 = addStep('Restoring original strain', `Returning mother to "${originalStrain.name}"...`);

        await api.restoreStrain(plant.Id, originalStrain.id);
        updateStep(step8, 'Strain restored', `Mother plant restored to "${originalStrain.name}"`, 'completed');

        // Step 9: Save breeding run
        const step9 = addStep('Saving breeding run', 'Persisting run data...');

        const breedingRun: BreedingWorkflowResult = {
          runType: 'seed_production_self',
          generationType: 'S',
          filialGeneration: strainData.generation,
          motherPlant: plant,
          originalStrain,
          newStrain: {
            id: strainId,
            name: strainData.newName,
          },
          seedsCreated: seedCount,
          roomId,
          roomName,
          notes: notes ?? undefined,
          destroyedMother: false,
          timestamp: new Date().toISOString(),
        };

        const saveResult = await api.saveBreedingRun(breedingRun);
        setState((prev) => ({
          ...prev,
          breedingRunId: saveResult.runId,
          breedingRunData: breedingRun,
        }));

        updateStep(step9, 'Run saved', 'Breeding run data saved successfully', 'completed');

        // Show summary (configurable delay for UX)
        setTimeout(() => setCurrentView('summary'), summaryDelay);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An error occurred');
        setTimeout(() => setCurrentView('form'), 3000);
      }
    },
    [summaryDelay, addStep, updateStep, createOrGetStrain, getRoomName]
  );

  const prepareHybridReview = useCallback(
    async (input: BreedingWorkflowInput, seedParent: Plant, pollenDonor: Plant) => {
      if (seedParent.Id === pollenDonor.Id) {
        setError('Seed parent and pollen donor must be different plants for an F1 cross');
        setTimeout(() => setCurrentView('form'), 3000);
        return;
      }

      const donorStep = addStep('Validating pollen donor', 'Checking if pollen donor can be used for breeding...');
      const donorValidation = await api.validatePlant(pollenDonor.Id);

      if (!donorValidation.valid) {
        updateStep(donorStep, 'Pollen donor validation failed', donorValidation.error || 'Pollen donor is not valid', 'error');
        setError(donorValidation.error || 'Pollen donor is not valid');
        setTimeout(() => setCurrentView('form'), 3000);
        return;
      }

      updateStep(donorStep, 'Pollen donor validated', 'Pollen donor is eligible for breeding', 'completed');

      const generated = generateF1HybridStrainName(seedParent.StrainName, pollenDonor.StrainName);
      const finalName = input.strainNameOverride?.trim() || generated.newName;

      setHybridReview({
        input,
        seedParent,
        pollenDonor,
        generatedName: generated.newName,
        finalName,
      });
      setState((prev) => ({
        ...prev,
        mode: 'f1Hybrid',
        selectedPlant: seedParent,
        originalStrain: { id: seedParent.StrainId, name: seedParent.StrainName },
        newStrain: { ...generated, newName: finalName, id: 0 },
        pollenDonorPlant: pollenDonor,
        pollenDonorStrain: { id: pollenDonor.StrainId, name: pollenDonor.StrainName },
        generatedStrainName: generated.newName,
        strainNameOverride: input.strainNameOverride?.trim() || '',
        seedCount: input.seedCount,
        roomId: input.roomId,
        notes: input.notes,
      }));

      addStep('F1 setup ready', `Review proposed strain name "${finalName}" before creating seeds.`, 'completed');
      setCurrentView('hybridReview');
    },
    [addStep, updateStep]
  );

  const continueHybridWithSeedParent = useCallback(
    async (input: BreedingWorkflowInput, seedParent: Plant) => {
      setCurrentView('progress');

      const seedParentStep = addStep('Validating seed parent', 'Checking if seed parent can be used for breeding...');
      const seedParentValidation = await api.validatePlant(seedParent.Id);

      if (!seedParentValidation.valid) {
        updateStep(seedParentStep, 'Seed parent validation failed', seedParentValidation.error || 'Seed parent is not valid', 'error');
        setError(seedParentValidation.error || 'Seed parent is not valid');
        setTimeout(() => setCurrentView('form'), 3000);
        return;
      }

      updateStep(seedParentStep, 'Seed parent validated', 'Seed parent is eligible for breeding', 'completed');

      const donorBarcode = input.pollenDonorBarcode || '';
      const donorSearchStep = addStep('Searching for pollen donor', `Looking for pollen donor barcode ending in "${donorBarcode}"...`);
      const donorSearch = await api.searchPlant(donorBarcode);

      if (donorSearch.plants.length === 0) {
        updateStep(donorSearchStep, 'Pollen donor not found', `No plants found with barcode ending in "${donorBarcode}"`, 'error');
        setError(`No pollen donor found with barcode ending in "${donorBarcode}"`);
        setTimeout(() => setCurrentView('form'), 3000);
        return;
      }

      if (donorSearch.plants.length > 1) {
        updateStep(
          donorSearchStep,
          'Multiple pollen donors found',
          `Found ${donorSearch.plants.length} matching plants. Please select the pollen donor.`,
          'completed'
        );
        setSelectionContext({ purpose: 'hybridPollenDonor', input, seedParent });
        setMultiplePlants(donorSearch.plants);
        setCurrentView('plantSelection');
        return;
      }

      const pollenDonor = donorSearch.plants[0];
      updateStep(donorSearchStep, 'Pollen donor found', `${pollenDonor.TSID} - ${pollenDonor.StrainName}`, 'completed');
      await prepareHybridReview(input, seedParent, pollenDonor);
    },
    [addStep, updateStep, prepareHybridReview]
  );

  const startHybridWorkflow = useCallback(
    async (input: BreedingWorkflowInput) => {
      setState((prev) => ({
        ...prev,
        mode: 'f1Hybrid',
        seedCount: input.seedCount,
        roomId: input.roomId,
        notes: input.notes,
        strainNameOverride: input.strainNameOverride?.trim() || '',
      }));
      setCurrentView('progress');
      setWorkflowSteps([]);
      setMultiplePlants([]);
      setSelectionContext(null);
      setHybridReview(null);
      setError('');

      const seedParentStep = addStep('Searching for seed parent', `Looking for seed parent barcode ending in "${input.barcode}"...`);
      const seedParentSearch = await api.searchPlant(input.barcode);

      if (seedParentSearch.plants.length === 0) {
        updateStep(seedParentStep, 'Seed parent not found', `No plants found with barcode ending in "${input.barcode}"`, 'error');
        setError(`No seed parent found with barcode ending in "${input.barcode}"`);
        setTimeout(() => setCurrentView('form'), 3000);
        return;
      }

      if (seedParentSearch.plants.length > 1) {
        updateStep(
          seedParentStep,
          'Multiple seed parents found',
          `Found ${seedParentSearch.plants.length} matching plants. Please select the seed parent.`,
          'completed'
        );
        setSelectionContext({ purpose: 'hybridSeedParent', input });
        setMultiplePlants(seedParentSearch.plants);
        setCurrentView('plantSelection');
        return;
      }

      const seedParent = seedParentSearch.plants[0];
      updateStep(seedParentStep, 'Seed parent found', `${seedParent.TSID} - ${seedParent.StrainName}`, 'completed');
      await continueHybridWithSeedParent(input, seedParent);
    },
    [addStep, updateStep, continueHybridWithSeedParent]
  );

  // Main workflow entry point. Existing tests still call the legacy signature.
  const startWorkflow = useCallback(
    async (
      inputOrBarcode: BreedingWorkflowInput | string,
      seedCount?: number,
      roomId?: number,
      notes: BreedingNotes | null = null
    ) => {
      const input = normalizeWorkflowInput(inputOrBarcode, seedCount, roomId, notes);

      if (!/^\d{4}$/.test(input.barcode)) {
        setError(
          input.mode === 'f1Hybrid'
            ? 'Please enter exactly 4 digits for the seed parent barcode'
            : 'Please enter exactly 4 digits for the barcode'
        );
        return;
      }

      if (input.mode === 'f1Hybrid' && !/^\d{4}$/.test(input.pollenDonorBarcode || '')) {
        setError('Please enter exactly 4 digits for the pollen donor barcode');
        return;
      }

      if (!input.seedCount || input.seedCount < 1 || input.seedCount > 100000) {
        setError('Please enter a valid seed count (1-100,000)');
        return;
      }

      if (!input.roomId) {
        setError('Please select a destination room for seeds');
        return;
      }

      if (input.mode === 'f1Hybrid') {
        await startHybridWorkflow(input);
        return;
      }

      setState((prev) => ({
        ...prev,
        mode: 'self',
        seedCount: input.seedCount,
        roomId: input.roomId,
        notes: input.notes,
        pollenDonorPlant: null,
        pollenDonorStrain: null,
        generatedStrainName: '',
        strainNameOverride: '',
      }));
      setCurrentView('progress');
      setWorkflowSteps([]);
      setMultiplePlants([]);
      setSelectionContext(null);
      setHybridReview(null);
      setError('');

      try {
        const step1 = addStep('Searching for plant', `Looking for plant with barcode ending in "${input.barcode}"...`);
        const searchData = await api.searchPlant(input.barcode);

        if (searchData.plants.length === 0) {
          updateStep(step1, 'Plant not found', `No plants found with barcode ending in "${input.barcode}"`, 'error');
          setError(`No plants found with barcode ending in "${input.barcode}"`);
          setTimeout(() => setCurrentView('form'), 3000);
          return;
        }

        if (searchData.plants.length > 1) {
          updateStep(step1, 'Multiple plants found', `Found ${searchData.plants.length} matching plants. Please select one.`, 'completed');
          setSelectionContext({ purpose: 'self', input });
          setMultiplePlants(searchData.plants);
          setCurrentView('plantSelection');
          return;
        }

        const plant = searchData.plants[0];
        updateStep(step1, 'Plant found', `${plant.TSID} - ${plant.StrainName}`, 'completed');
        await executeSelfWorkflow(plant, input.seedCount, input.roomId, input.notes, state.seedRooms);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An error occurred');
        setTimeout(() => setCurrentView('form'), 3000);
      }
    },
    [addStep, updateStep, executeSelfWorkflow, startHybridWorkflow, state.seedRooms]
  );

  const continueWithPlant = useCallback(
    async (plant: Plant) => {
      const context = selectionContext;
      if (!context) {
        return;
      }

      setCurrentView('progress');

      if (context.purpose === 'self') {
        addStep('Plant selected', `${plant.TSID} - ${plant.StrainName}`, 'completed');
        await executeSelfWorkflow(plant, context.input.seedCount, context.input.roomId, context.input.notes, state.seedRooms);
        return;
      }

      if (context.purpose === 'hybridSeedParent') {
        addStep('Seed parent selected', `${plant.TSID} - ${plant.StrainName}`, 'completed');
        await continueHybridWithSeedParent(context.input, plant);
        return;
      }

      if (context.purpose === 'hybridPollenDonor' && context.seedParent) {
        addStep('Pollen donor selected', `${plant.TSID} - ${plant.StrainName}`, 'completed');
        await prepareHybridReview(context.input, context.seedParent, plant);
      }
    },
    [
      selectionContext,
      state.seedRooms,
      addStep,
      executeSelfWorkflow,
      continueHybridWithSeedParent,
      prepareHybridReview,
    ]
  );

  const confirmHybridWorkflow = useCallback(
    async (finalName: string) => {
      if (!hybridReview) {
        setError('No F1 hybrid setup is ready to confirm');
        return;
      }

      const strainName = finalName.trim();
      if (!strainName) {
        setError('Please enter a strain name for the F1 seeds');
        return;
      }

      const { input, seedParent, pollenDonor, generatedName } = hybridReview;
      const originalStrain = { id: seedParent.StrainId, name: seedParent.StrainName };
      const pollenDonorStrain = { id: pollenDonor.StrainId, name: pollenDonor.StrainName };
      const generatedData: StrainProgression = {
        ...generateF1HybridStrainName(seedParent.StrainName, pollenDonor.StrainName),
        newName: strainName,
      };

      setCurrentView('progress');
      setError('');
      setState((prev) => ({
        ...prev,
        mode: 'f1Hybrid',
        selectedPlant: seedParent,
        originalStrain,
        newStrain: { ...generatedData, id: 0 },
        pollenDonorPlant: pollenDonor,
        pollenDonorStrain,
        generatedStrainName: generatedName,
        strainNameOverride: strainName === generatedName ? '' : strainName,
        seedCount: input.seedCount,
        roomId: input.roomId,
        notes: input.notes,
      }));

      try {
        addStep('Confirming F1 name', `Using "${strainName}" for the new F1 seed inventory.`, 'completed');

        const strainId = await createOrGetStrain(strainName, 'Creating F1 strain');

        setState((prev) => ({
          ...prev,
          newStrain: prev.newStrain ? { ...prev.newStrain, id: strainId } : null,
        }));

        const motherStep = addStep('Checking seed parent mother status', 'Verifying seed parent mother status...');
        if (seedParent.IsMother) {
          updateStep(motherStep, 'Already a mother', 'Seed parent is already a mother plant, skipping', 'completed');
        } else {
          updateStep(motherStep, 'Converting seed parent to mother', 'Toggling mother plant status...', 'processing');
          await api.toggleMotherStatus(seedParent.Id);
          updateStep(motherStep, 'Mother status updated', 'Seed parent converted to mother status', 'completed');
        }

        const reassignStep = addStep('Reassigning seed parent strain', `Temporarily assigning to "${strainName}"...`);
        await api.reassignStrain(seedParent.Id, strainId);
        updateStep(reassignStep, 'Seed parent reassigned', `Seed parent reassigned to "${strainName}"`, 'completed');

        const roomName = getRoomName(input.roomId, state.seedRooms);
        const seedStep = addStep('Creating F1 seeds', `Creating ${input.seedCount} F1 seeds in ${roomName}...`);
        await api.createSeeds(seedParent.Id, input.seedCount, input.roomId);
        updateStep(seedStep, 'F1 seeds created', `Created ${input.seedCount} F1 seeds in ${roomName}`, 'completed');

        const restoreStep = addStep('Restoring seed parent strain', `Returning seed parent to "${originalStrain.name}"...`);
        await api.restoreStrain(seedParent.Id, originalStrain.id);
        updateStep(restoreStep, 'Seed parent strain restored', `Seed parent restored to "${originalStrain.name}"`, 'completed');

        const saveStep = addStep('Saving F1 breeding run', 'Persisting F1 parentage and seed run data...');
        const breedingRun: BreedingWorkflowResult = {
          runType: 'seed_production_cross',
          generationType: 'F',
          filialGeneration: 1,
          motherPlant: seedParent,
          originalStrain,
          newStrain: {
            id: strainId,
            name: strainName,
          },
          pollenDonorPlant: pollenDonor,
          pollenDonorStrain,
          generatedStrainName: generatedName,
          strainNameOverride: strainName === generatedName ? null : strainName,
          seedsCreated: input.seedCount,
          roomId: input.roomId,
          roomName,
          notes: input.notes ?? undefined,
          destroyedMother: false,
          timestamp: new Date().toISOString(),
        };

        const saveResult = await api.saveBreedingRun(breedingRun);
        setState((prev) => ({
          ...prev,
          breedingRunId: saveResult.runId,
          breedingRunData: breedingRun,
        }));
        updateStep(saveStep, 'F1 run saved', 'F1 parentage and seed run data saved successfully', 'completed');

        setTimeout(() => setCurrentView('summary'), summaryDelay);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An error occurred');
        setTimeout(() => setCurrentView('form'), 3000);
      }
    },
    [hybridReview, state.seedRooms, summaryDelay, addStep, updateStep, createOrGetStrain, getRoomName]
  );

  const startOver = useCallback(() => {
    setState(buildInitialState(state.seedRooms));
    setCurrentView('form');
    setWorkflowSteps([]);
    setMultiplePlants([]);
    setSelectionContext(null);
    setHybridReview(null);
    setError('');
  }, [state.seedRooms]);

  const updateBreedingRunData = useCallback((updates: Partial<BreedingWorkflowResult>) => {
    setState((prev) => ({
      ...prev,
      breedingRunData: prev.breedingRunData ? { ...prev.breedingRunData, ...updates } : null,
    }));

    // Persist updates if we have a run ID
    if (state.breedingRunId && state.breedingRunData) {
      const updatedRun = { ...state.breedingRunData, ...updates, id: state.breedingRunId };
      api.saveBreedingRun(updatedRun).catch((err) => {
        console.error('Failed to persist breeding run updates:', err);
      });
    }
  }, [state.breedingRunId, state.breedingRunData]);

  const plantSelectionTitle =
    selectionContext?.purpose === 'hybridSeedParent'
      ? 'Select Seed Parent'
      : selectionContext?.purpose === 'hybridPollenDonor'
        ? 'Select Pollen Donor'
        : 'Select Plant';

  const plantSelectionDescription =
    selectionContext?.purpose === 'hybridSeedParent'
      ? 'Multiple plants matched the seed parent barcode. Select the plant that will receive pollen and create seeds.'
      : selectionContext?.purpose === 'hybridPollenDonor'
        ? 'Multiple plants matched the pollen donor barcode. Select the stud or donor plant for this F1 cross.'
        : 'Multiple plants found. Please select one:';

  return {
    state,
    currentView,
    workflowSteps,
    multiplePlants,
    plantSelectionTitle,
    plantSelectionDescription,
    hybridReview,
    error,
    startWorkflow,
    continueWithPlant,
    confirmHybridWorkflow,
    startOver,
    updateBreedingRunData,
    clearError: () => setError(''),
  };
}
